// ─── Family Planner domain types ─────────────────────────────

export type CalendarProvider = 'google' | 'microsoft' | 'ics' | 'local'
export type IngestChannel = 'gmail' | 'whatsapp'
export type ConnectionStatus = 'connected' | 'expired' | 'error'

export type EventCategory =
  | 'school'
  | 'sports'
  | 'medical'
  | 'work'
  | 'social'
  | 'travel'
  | 'other'

export const EVENT_CATEGORIES: EventCategory[] = [
  'school',
  'sports',
  'medical',
  'work',
  'social',
  'travel',
  'other',
]

// ─── Family members ──────────────────────────────────────────

export interface FamilyMember {
  id: string
  name: string
  /** Tailwind-independent hex used for calendar chips and legends */
  color: string
}

// ─── Calendar accounts & calendars ───────────────────────────

export interface LinkedCalendar {
  id: string
  name: string
  provider: CalendarProvider
  accountEmail: string
  color: string
  /** Unchecked calendars stay linked but drop out of the aggregate view */
  enabled: boolean
  /** True for subscribed/holiday calendars the provider won't let us write */
  readOnly: boolean
  /** Family member this calendar primarily belongs to, if any */
  memberId: string | null
}

export interface CalendarConnection {
  id: string
  provider: CalendarProvider
  status: ConnectionStatus
  accountEmail: string
  /** Human-readable scope summary shown before the OAuth handoff */
  scopeSummary: string
  calendarIds: string[]
  lastSync: string | null
  /** Set when status is 'error' — surfaced verbatim, never swallowed */
  error?: string
}

// ─── Aggregated events ───────────────────────────────────────

export interface Attendee {
  name: string
  email?: string
  response?: 'accepted' | 'declined' | 'tentative' | 'needs-action'
}

export interface PlannerEvent {
  id: string
  title: string
  /** ISO 8601 local datetime, or YYYY-MM-DD when allDay */
  start: string
  end: string
  allDay: boolean
  location?: string
  notes?: string
  calendarId: string
  /** Where the event entered the planner */
  origin: 'calendar' | IngestChannel | 'manual'
  /** Provider message/thread id the event was extracted from */
  originRef?: string
  organizer?: string
  attendees: Attendee[]
  memberIds: string[]
  category: EventCategory
  /** Ids of same-event copies collapsed into this one (cross-calendar dupes) */
  mergedFrom?: string[]
  updatedAt: string
}

export interface EventConflict {
  id: string
  memberId: string
  a: PlannerEvent
  b: PlannerEvent
  overlapMinutes: number
}

// ─── Ingest: Gmail invitations & WhatsApp threads ────────────

/**
 * A saved scan target. For Gmail this is a Gmail search query; for WhatsApp it
 * is a thread/group name that the bridge is allowed to read (see
 * docs/family-planner-architecture.md — personal chats are not readable via
 * any WhatsApp API, so a thread only becomes scannable through the forwarding
 * address or the Cloud API family number).
 */
export interface ScanRule {
  id: string
  channel: IngestChannel
  label: string
  /** Gmail query string, or WhatsApp thread name */
  query: string
  enabled: boolean
  lastScan: string | null
  matchCount: number
  /** Default calendar accepted events land on */
  targetCalendarId: string
  /** Members auto-tagged onto events from this rule */
  memberIds: string[]
}

export interface ParsedEventDraft {
  title: string
  start: string
  end: string
  allDay: boolean
  location?: string
  notes?: string
  attendees: Attendee[]
  memberIds: string[]
  category: EventCategory
}

/**
 * Nothing scanned out of email or chat is written to the calendar on its own —
 * every extraction lands here and waits for an explicit accept.
 */
export interface EventCandidate {
  id: string
  channel: IngestChannel
  receivedAt: string
  from: string
  /** Email subject, or WhatsApp thread name */
  context: string
  /** Raw excerpt the parse came from, shown so the user can check the work */
  excerpt: string
  /** 0–1 parser confidence; low-confidence rows are visually flagged */
  confidence: number
  parsed: ParsedEventDraft
  status: 'pending' | 'accepted' | 'rejected'
  targetCalendarId: string
  /** Set when an existing event already covers this — prevents double-booking */
  duplicateOfEventId?: string
  ruleId?: string
}

// ─── Phase 2: travel & getaways ──────────────────────────────

export type DealKind = 'flight' | 'cruise' | 'package'

export interface Destination {
  id: string
  name: string
  region: string
  /** Which deal kinds to watch for this destination */
  kinds: DealKind[]
  /** Origin airports to price from (flights only) */
  originAirports: string[]
  destinationAirports: string[]
  /** Alert threshold — total trip cost for the family */
  maxBudget: number
  minNights: number
  maxNights: number
  enabled: boolean
  notes?: string
}

export interface TravelDeal {
  id: string
  destinationId: string
  kind: DealKind
  title: string
  /** 'Google Flights', 'Royal Caribbean', … */
  provider: string
  price: number
  /** Typical/median price for the same route+season, for the discount math */
  typicalPrice: number
  departDate: string
  returnDate: string
  nights: number
  origin?: string
  url: string
  /** Free-text extra: 'Balcony, 2 cabins', '1 stop, 6h 20m' */
  detail: string
  foundAt: string
  expiresAt?: string
}

export interface ScoredDeal extends TravelDeal {
  /** Percent below typical price (0.28 = 28% off) */
  discount: number
  /** 0–100 composite: discount, budget fit, calendar fit */
  score: number
  /** The free window this trip fits inside, if any */
  windowId: string | null
  /** Events that would have to move if this trip were booked */
  blockingEvents: PlannerEvent[]
  withinBudget: boolean
}

export interface FreeWindow {
  id: string
  start: string
  end: string
  nights: number
  /** Members with nothing scheduled across the whole window */
  freeMemberIds: string[]
  /** Members who do have something — the window is partial for them */
  busyMemberIds: string[]
  label: string
}

// ─── AI assistant ────────────────────────────────────────────

export interface AssistantCitation {
  label: string
  eventId?: string
  dealId?: string
}

export interface AssistantMessage {
  id: string
  role: 'user' | 'assistant'
  text: string
  createdAt: string
  citations: AssistantCitation[]
  /** Follow-up prompts offered as chips under the answer */
  suggestions?: string[]
  /** True when answered by the offline rules engine rather than the model */
  isOffline?: boolean
}
